import * as React from 'react';
import * as _ from 'lodash';
import * as classnames from 'classnames';
import { Audience, Project } from '@nexxt/common/types';
import {
    PREDEFINED_GROUP_TYPES,
    PREDEFINED_GROUP_TITLE,
    TARGET_GROUPS
} from '../../../constants/audience';
import { dataTestid } from '../../../constants/dataTestid';
import ValidationService from '@nexxt/common/services/ValidationService';

interface PreDefinedAudienceGroupProps {
    audience: Audience;
    showError: boolean;
    editEnabled: boolean;
    enableLoi: boolean;
    validationService: ValidationService;
    handleProjectSettings: (keyName: string, value: any) => void;
}

const PreDefinedAudienceGroup: React.FC<PreDefinedAudienceGroupProps> = (
    props: PreDefinedAudienceGroupProps
) => {
    const handleTargetGroup = (value, addsOn = false) => {
        const { audience, handleProjectSettings } = props;
        const predefinedAudience = audience.predefinedAudience;
        let newPredefinedGroup = _.cloneDeep(predefinedAudience);
        // always replace index 0
        if (!addsOn) {
            newPredefinedGroup[0] = value;
        }
        if (addsOn) {
            const hasSameGroupValue = predefinedAudience.find(
                (a) => a.groupValue === value.groupValue
            );

            if (hasSameGroupValue) {
                const hasSameGroup = predefinedAudience.find(
                    (a) =>
                        a.groupValue === value.groupValue &&
                        a.totalCount === value.totalCount
                );
                newPredefinedGroup = newPredefinedGroup.filter(
                    (x) => x.groupValue !== value.groupValue
                );
                if (!hasSameGroup) {
                    newPredefinedGroup = [...newPredefinedGroup, value];
                }
            } else {
                newPredefinedGroup = [...newPredefinedGroup, value];
            }
        }

        handleProjectSettings('predefinedAudience', newPredefinedGroup);
    };

    const {
        audience,
        showError,
        editEnabled,
        enableLoi,
        validationService,
        handleProjectSettings
    } = props;

    const segmentGroups = TARGET_GROUPS[PREDEFINED_GROUP_TYPES.SEGMENT];
    const addOnGroups = TARGET_GROUPS[PREDEFINED_GROUP_TYPES.ADD_ON];
    return (
        <>
            <div className="row">
                <div
                    className={`form-group col-md-6 ${
                        (validationService.isEmpty(
                            audience.lengthOfInterview
                        ) ||
                            !audience.lengthOfInterview) &&
                        showError
                            ? 'error'
                            : ''
                    }`}
                >
                    {enableLoi && (
                        <>
                            <label className="form-group__label form-group--block">
                                Video Duration in Seconds
                            </label>
                            <input
                                type="number"
                                name="lengthOfInterview"
                                placeholder=""
                                data-test={dataTestid.VIDEO_DURATION_INPUT}
                                className="form-group__control"
                                value={audience.lengthOfInterview}
                                pattern="[0-9]*"
                                onChange={(e) =>
                                    handleProjectSettings(
                                        'lengthOfInterview',
                                        parseInt(e.target.value)
                                    )
                                }
                                defaultValue=""
                                disabled={!editEnabled}
                            />
                        </>
                    )}
                    <span className="form-group__error-msg"></span>
                </div>
            </div>
            <div className="row">
                <div
                    className={`col-md-12 ${
                        !audience.predefinedAudience.length && showError
                            ? 'error'
                            : ''
                    }`}
                >
                    <div className="audience--section-container">
                        <p>Select a pre-defined audience</p>

                        <div className="predefined-group--type-wrapper">
                            <p className="predefined-group--title">
                                <b>
                                    {
                                        PREDEFINED_GROUP_TITLE[
                                            PREDEFINED_GROUP_TYPES.SEGMENT
                                        ].title
                                    }
                                </b>
                                (
                                {
                                    PREDEFINED_GROUP_TITLE[
                                        PREDEFINED_GROUP_TYPES.SEGMENT
                                    ].subTitle
                                }
                                )
                                <span className="predefined-group--title required">
                                    * required
                                </span>
                            </p>
                            <div className="row predefined-group--wrapper">
                                {Object.keys(segmentGroups).map((title) => {
                                    const subGroups = segmentGroups[title];
                                    return (
                                        <div
                                            key={title}
                                            className="predefined-group--category-wrapper"
                                        >
                                            {subGroups.map((subGroup, ind) => (
                                                <button
                                                    key={`${title}-${ind}`}
                                                    className={classnames(
                                                        'button button-large',
                                                        {
                                                            selected:
                                                                audience.predefinedAudience.find(
                                                                    (
                                                                        pAudience
                                                                    ) =>
                                                                        pAudience.groupValue ===
                                                                            subGroup.groupValue &&
                                                                        pAudience.price ===
                                                                            subGroup.price
                                                                )
                                                        }
                                                    )}
                                                    style={{
                                                        float: 'none',
                                                        alignContent: 'center'
                                                    }}
                                                    onClick={() =>
                                                        handleTargetGroup(
                                                            subGroup
                                                        )
                                                    }
                                                    disabled={!editEnabled}
                                                >
                                                    <span className="title">
                                                        <b> {subGroup.label}</b>
                                                    </span>
                                                    <span>
                                                        {subGroup?.subLabel}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="predefined-group--type-wrapper">
                            <p className="predefined-group--title">
                                <b>
                                    {
                                        PREDEFINED_GROUP_TITLE[
                                            PREDEFINED_GROUP_TYPES.ADD_ON
                                        ].title
                                    }
                                </b>
                                (
                                {
                                    PREDEFINED_GROUP_TITLE[
                                        PREDEFINED_GROUP_TYPES.ADD_ON
                                    ].subTitle
                                }
                                )
                            </p>
                            <div className="row predefined-group--wrapper">
                                {Object.keys(addOnGroups).map((title) => {
                                    const subGroups = addOnGroups[title];

                                    return (
                                        <div
                                            key={title}
                                            className="predefined-group--category-wrapper"
                                            style={{ display: 'block' }}
                                        >
                                            <p>
                                                <b>{title}</b>
                                            </p>
                                            {subGroups.map((subGroup, ind) => (
                                                <button
                                                    key={`${title}-${ind}`}
                                                    className={classnames(
                                                        'button button-small',
                                                        {
                                                            selected:
                                                                audience.predefinedAudience.find(
                                                                    (
                                                                        pAudience
                                                                    ) =>
                                                                        pAudience.groupValue ===
                                                                            subGroup.groupValue &&
                                                                        pAudience.totalCount ===
                                                                            subGroup.totalCount
                                                                )
                                                        }
                                                    )}
                                                    style={{
                                                        float: 'none'
                                                    }}
                                                    onClick={() =>
                                                        handleTargetGroup(
                                                            subGroup,
                                                            true
                                                        )
                                                    }
                                                    disabled={
                                                        !audience
                                                            .predefinedAudience
                                                            .length
                                                    }
                                                >
                                                    <span className="title">
                                                        <b>{`N = ${subGroup.totalCount}`}</b>
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PreDefinedAudienceGroup;
